import { useAtomValue } from "jotai";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";

import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";

import PageAPI from "../../api/PageAPI";
import { AppConfirm } from "../../../utils/PopupUtil";
import { newToastEvent } from "../../../utils/ToastUtil";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";

const useDeleteView = (pageID: string, currentViewID?: string) => {
  const workspaceID = useAtomValue(workspaceIdAtom);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantID }: any = useParams();

  const deleteView = useMutation({
    mutationFn: async ({
      viewID,
      confirm = true,
      parentViewID,
    }: {
      parentViewID?: string;
      viewID: string;
      confirm?: boolean;
      onSuccess?: () => void;
    }) => {
      if (!confirm) {
        return PageAPI.deleteView(workspaceID, pageID, viewID).then((data) => {
          const newPage = data.data.data.page;
          return { newPage, viewID };
        });
      }

      const result = await AppConfirm({
        title: "Confirm View Deletion",
        html: "Are you sure you want to delete this View? <br/> All content inside will be lost.",
      });

      if (!result.isConfirmed) return Promise.reject();

      if (parentViewID) {
        return PageAPI.deleteSubView(
          workspaceID,
          pageID,
          parentViewID,
          viewID
        ).then((data) => {
          const newView = data.data.data.view;
          return { newView, newPage: null, viewID: null };
        });
      }

      return PageAPI.deleteView(workspaceID, pageID, viewID).then((data) => {
        const newPage = data.data.data.page;
        return { newPage, viewID, newView: null };
      });
    },
    onSuccess: ({ newPage, viewID, newView }: any, { onSuccess }) => {
      newPage && queryClient.setQueryData(["page", pageID], newPage);
      newView && queryClient.setQueryData(["view", newView?._id], newView);
      newToastEvent("success", "Successfully deleted view.");
      if (currentViewID === viewID) {
        navigate(
          `/clouds/${tenantID}/workspaces/${workspaceID}/pages/${pageID}/views/${newPage.Views[0]?.id}`
        );
      }
      callFunctionIfExists(onSuccess);
    },
  });

  return { deleteView: deleteView.mutate, viewDeleting: deleteView.isPending };
};

export default useDeleteView;
